type AbortSignalWithReason = AbortSignal & {
  reason?: unknown
}

const createTimeoutError = () => {
  const error = new Error("요청 시간이 초과되었습니다.")
  error.name = "TimeoutError"
  return error
}

const createAbortError = () => {
  const error = new Error("요청이 취소되었습니다.")
  error.name = "AbortError"
  return error
}

const getAbortReason = (signal?: AbortSignal | null) => {
  const reason = (signal as AbortSignalWithReason | null)?.reason
  if (reason instanceof Error) {
    return reason
  }

  return createAbortError()
}

export async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = 5000): Promise<Response> {
  if (typeof AbortController === "undefined") {
    return await Promise.race([
      fetch(input, init),
      new Promise<Response>((_, reject) => {
        setTimeout(() => reject(createTimeoutError()), timeoutMs)
      }),
    ])
  }

  const { signal: externalSignal, ...requestInit } = init
  const controller = new AbortController()
  const timeoutError = createTimeoutError()
  let timedOut = false
  let externalAbortError: Error | null = null
  const timeoutId = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, timeoutMs)

  const handleExternalAbort = () => {
    externalAbortError = getAbortReason(externalSignal)
    controller.abort()
  }

  if (externalSignal) {
    if (externalSignal.aborted) {
      handleExternalAbort()
    } else {
      externalSignal.addEventListener("abort", handleExternalAbort, { once: true })
    }
  }

  try {
    return await fetch(input, {
      ...requestInit,
      signal: controller.signal,
    })
  } catch (error) {
    if (timedOut) {
      throw timeoutError
    }

    if (externalAbortError) {
      throw externalAbortError
    }

    throw error
  } finally {
    clearTimeout(timeoutId)
    externalSignal?.removeEventListener("abort", handleExternalAbort)
  }
}
